import type { IToDoSchedulePost, ToDoScheduleAssignmentType } from 'core/API/recurringTodo';

export const RECURRING_FREQUENCY_OPTIONS = [
	'Daily',
	'Weekday',
	'Weekly',
	'Monthly',
	'Annually',
] as const;

export type RecurringFrequencyLabel = (typeof RECURRING_FREQUENCY_OPTIONS)[number];

export const WEEKDAY_OPTIONS = [
	'Monday',
	'Tuesday',
	'Wednesday',
	'Thursday',
	'Friday',
	'Saturday',
	'Sunday',
] as const;

export const MONTH_OPTIONS = [
	{ value: 1, label: 'January' },
	{ value: 2, label: 'February' },
	{ value: 3, label: 'March' },
	{ value: 4, label: 'April' },
	{ value: 5, label: 'May' },
	{ value: 6, label: 'June' },
	{ value: 7, label: 'July' },
	{ value: 8, label: 'August' },
	{ value: 9, label: 'September' },
	{ value: 10, label: 'October' },
	{ value: 11, label: 'November' },
	{ value: 12, label: 'December' },
];

export const MONTH_PATTERN_WEEKS = ['first', 'second', 'third', 'fourth', 'last'] as const;

export type MonthlyScheduleType = 'specific_dates' | 'recurring_days';

export interface RecurrenceFormState {
	frequencyLabel: RecurringFrequencyLabel;
	frequencyId: number;
	scheduledTime: string;
	dayOfWeek: string;
	dayOfMonth: number;
	annualMonth: number;
	annualDay: number;
	monthlyScheduleType: MonthlyScheduleType;
	monthPatternWeek: string;
	monthPatternDay: string;
}

export const DEFAULT_RECURRENCE_FORM: RecurrenceFormState = {
	frequencyLabel: 'Daily',
	frequencyId: 1,
	scheduledTime: '09:00:00',
	dayOfWeek: 'Monday',
	dayOfMonth: 1,
	annualMonth: 1,
	annualDay: 1,
	monthlyScheduleType: 'specific_dates',
	monthPatternWeek: 'second',
	monthPatternDay: 'Thursday',
};

const EXCLUDED_RECURRING_FREQUENCY_LABELS = new Set(['once', 'quarterly']);

const EMPTY_SCHEDULE_FREQUENCY_FIELDS = {
	dailyTimes: null,
	weeklyTimes: null,
	weekdayTimes: null,
	monthlyScheduleType: null,
	monthlyScheduleSpecificDates: null,
	monthlyScheduleRecurringDays: null,
	annualDate: null,
	annualTime: null,
};

const toHourMinute = (time: string): string => {
	if (!time) return '09:00';
	const parts = time.split(':');
	if (parts.length >= 2) {
		return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
	}
	return time;
};

const toHourMinuteSecond = (time: string): string => {
	const hourMinute = toHourMinute(time);
	return hourMinute.length === 5 ? `${hourMinute}:00` : hourMinute;
};

const getRecurringNumber = (week: string): number => {
	const recurringMap: Record<string, number> = {
		first: 1,
		second: 2,
		third: 3,
		fourth: 4,
		last: 5,
	};
	return recurringMap[week.toLowerCase()] || 1;
};

export const getWeekFromRecurringNumber = (value: number): string => {
	const weeks = [...MONTH_PATTERN_WEEKS];
	return weeks[value - 1] || 'second';
};

const capitalizeDay = (day: string): string =>
	day ? day.charAt(0).toUpperCase() + day.slice(1).toLowerCase() : 'Monday';

const capitalizeFirstWord = (text: string): string => {
	if (!text) return text;
	return text.charAt(0).toUpperCase() + text.slice(1);
};

export const filterRecurringFrequencies = (frequencies: unknown[]): Array<{ id: number; label: string }> => {
	if (!Array.isArray(frequencies)) return [];

	return frequencies
		.map((item) => {
			if (!item || typeof item !== 'object') return null;
			const record = item as Record<string, unknown>;
			const id = Number(record.id);
			const label = String(record.label ?? record.name ?? '').trim();
			if (!Number.isFinite(id) || !label) return null;
			return { id, label };
		})
		.filter((item): item is { id: number; label: string } => item !== null)
		.filter((item) => !EXCLUDED_RECURRING_FREQUENCY_LABELS.has(item.label.toLowerCase()));
};

export const normalizeToDoSchedules = (data: unknown): Record<string, unknown>[] => {
	if (Array.isArray(data)) {
		return data as Record<string, unknown>[];
	}
	if (data && typeof data === 'object') {
		const record = data as Record<string, unknown>;
		if (Array.isArray(record.member)) return record.member as Record<string, unknown>[];
		if (Array.isArray(record['hydra:member'])) return record['hydra:member'] as Record<string, unknown>[];
	}
	return [];
};

export interface BuildToDoSchedulePayloadInput {
	description: string;
	note: string;
	priorityId: number;
	categoryIri: string;
	assignmentType: 'Person' | 'Group';
	selectedValue: number;
	recurrence: RecurrenceFormState;
}

export const buildToDoSchedulePayload = (input: BuildToDoSchedulePayloadInput): IToDoSchedulePost => {
	const workplaceId = Number(localStorage.getItem('workplace-id') || '1');
	const assignmentType: ToDoScheduleAssignmentType =
		input.assignmentType === 'Person' ? 'person' : 'group';
	const timeValue = toHourMinute(input.recurrence.scheduledTime);
	const timeWithSeconds = toHourMinuteSecond(input.recurrence.scheduledTime);

	const payload: IToDoSchedulePost = {
		description: input.description,
		note: input.note,
		priority: `/api/priorities/${input.priorityId}`,
		category: input.categoryIri,
		workplace: `/api/workplaces/${workplaceId}`,
		assignmentType,
		assignee: null,
		group: null,
		checklistFrequency: `/api/checklist_frequencies/${input.recurrence.frequencyId}`,
		pastDueAfterDay: 0,
		pastDueAfterHour: 'hours',
		...EMPTY_SCHEDULE_FREQUENCY_FIELDS,
	};

	if (input.assignmentType === 'Person') {
		payload.assignee = `/api/users/${input.selectedValue}`;
	} else {
		payload.group = `/api/checklist_groups/${input.selectedValue}`;
	}

	switch (input.recurrence.frequencyLabel) {
		case 'Daily':
			payload.dailyTimes = [timeWithSeconds];
			break;
		case 'Weekday':
			payload.weekdayTimes = [timeValue];
			break;
		case 'Weekly':
			payload.weeklyTimes = {
				[input.recurrence.dayOfWeek.toLowerCase()]: [timeValue],
			};
			break;
		case 'Monthly':
			if (input.recurrence.monthlyScheduleType === 'recurring_days') {
				payload.monthlyScheduleType = 'recurring_days';
				payload.monthlyScheduleRecurringDays = [
					{
						recurring: getRecurringNumber(input.recurrence.monthPatternWeek),
						day: input.recurrence.monthPatternDay.toLowerCase(),
						times: [timeValue],
					},
				];
			} else {
				const currentDate = new Date();
				const year = currentDate.getFullYear();
				const month = String(currentDate.getMonth() + 1).padStart(2, '0');
				const day = String(input.recurrence.dayOfMonth).padStart(2, '0');
				payload.monthlyScheduleType = 'specific_dates';
				payload.monthlyScheduleSpecificDates = [
					{
						date: `${year}-${month}-${day}`,
						times: [timeValue],
					},
				];
			}
			break;
		case 'Annually': {
			const year = new Date().getFullYear();
			const month = String(input.recurrence.annualMonth).padStart(2, '0');
			const day = String(input.recurrence.annualDay).padStart(2, '0');
			payload.annualDate = `${year}-${month}-${day}`;
			payload.annualTime = timeWithSeconds;
			break;
		}
		default:
			break;
	}

	return payload;
};

export const parseToDoScheduleToRecurrenceForm = (
	schedule: Record<string, unknown>,
	frequencyOptions: Array<{ id: number; label: string }>
): RecurrenceFormState => {
	const frequency = schedule.checklistFrequency as Record<string, unknown> | undefined;
	const frequencyLabel = String(frequency?.label ?? 'Daily') as RecurringFrequencyLabel;
	const frequencyMatch = frequencyOptions.find(
		(option) => option.label.toLowerCase() === frequencyLabel.toLowerCase()
	);

	const next: RecurrenceFormState = {
		...DEFAULT_RECURRENCE_FORM,
		frequencyLabel: RECURRING_FREQUENCY_OPTIONS.includes(frequencyLabel)
			? frequencyLabel
			: 'Daily',
		frequencyId: frequencyMatch?.id ?? Number(frequency?.id) ?? DEFAULT_RECURRENCE_FORM.frequencyId,
	};

	const dailyTimes = schedule.dailyTimes as string[] | null | undefined;
	if (Array.isArray(dailyTimes) && dailyTimes[0]) {
		next.scheduledTime = toHourMinuteSecond(String(dailyTimes[0]));
	}

	const weekdayTimes = schedule.weekdayTimes as string[] | null | undefined;
	if (Array.isArray(weekdayTimes) && weekdayTimes[0]) {
		next.scheduledTime = toHourMinuteSecond(String(weekdayTimes[0]));
	}

	const weeklyTimes = schedule.weeklyTimes as Record<string, string[]> | null | undefined;
	if (weeklyTimes && typeof weeklyTimes === 'object') {
		const day = Object.keys(weeklyTimes)[0];
		if (day) {
			next.dayOfWeek = day.charAt(0).toUpperCase() + day.slice(1);
			const times = weeklyTimes[day];
			if (Array.isArray(times) && times[0]) {
				next.scheduledTime = toHourMinuteSecond(String(times[0]));
			}
		}
	}

	const monthlyScheduleType = String(schedule.monthlyScheduleType ?? 'specific_dates');
	const recurringDays = schedule.monthlyScheduleRecurringDays as
		| Array<{ recurring?: number; day?: string; times?: string[] }>
		| null
		| undefined;

	if (monthlyScheduleType === 'recurring_days' && Array.isArray(recurringDays) && recurringDays[0]) {
		next.monthlyScheduleType = 'recurring_days';
		next.monthPatternWeek = getWeekFromRecurringNumber(Number(recurringDays[0].recurring));
		next.monthPatternDay = capitalizeDay(String(recurringDays[0].day ?? 'Thursday'));
		if (Array.isArray(recurringDays[0].times) && recurringDays[0].times?.[0]) {
			next.scheduledTime = toHourMinuteSecond(String(recurringDays[0].times[0]));
		}
	}

	const monthlyDates = schedule.monthlyScheduleSpecificDates as
		| Array<{ date?: string; times?: string[] }>
		| null
		| undefined;
	if (Array.isArray(monthlyDates) && monthlyDates[0]?.date) {
		next.monthlyScheduleType = 'specific_dates';
		const dateParts = String(monthlyDates[0].date).split('-');
		if (dateParts.length === 3) {
			next.dayOfMonth = Number(dateParts[2]) || DEFAULT_RECURRENCE_FORM.dayOfMonth;
		}
		if (Array.isArray(monthlyDates[0].times) && monthlyDates[0].times?.[0]) {
			next.scheduledTime = toHourMinuteSecond(String(monthlyDates[0].times[0]));
		}
	}

	if (schedule.annualDate) {
		const dateParts = String(schedule.annualDate).split('T')[0].split('-');
		if (dateParts.length === 3) {
			next.annualMonth = Number(dateParts[1]) || DEFAULT_RECURRENCE_FORM.annualMonth;
			next.annualDay = Number(dateParts[2]) || DEFAULT_RECURRENCE_FORM.annualDay;
		}
	}
	if (schedule.annualTime) {
		next.scheduledTime = toHourMinuteSecond(String(schedule.annualTime));
	}

	return next;
};

export const getScheduleAssignmentDisplay = (schedule: Record<string, unknown>): string => {
	if (schedule.assignmentType === 'person' || schedule.assignmentType === 'assignee') {
		const assignee = schedule.assignee as Record<string, unknown> | undefined;
		if (assignee) {
			return [assignee.firstName, assignee.middleName, assignee.lastName]
				.filter((part) => typeof part === 'string' && part.trim() !== '')
				.join(' ')
				.trim();
		}
	}
	const group = schedule.group as Record<string, unknown> | undefined;
	return String(group?.label ?? group?.name ?? 'Unassigned');
};

export const getScheduleFrequencyDisplay = (schedule: Record<string, unknown>): string => {
	const frequency = schedule.checklistFrequency as Record<string, unknown> | undefined;
	return String(frequency?.label ?? 'N/A');
};

export const getScheduleTimeDisplay = (schedule: Record<string, unknown>): string => {
	const dailyTimes = schedule.dailyTimes as string[] | null | undefined;
	if (Array.isArray(dailyTimes) && dailyTimes[0]) {
		return capitalizeFirstWord(toHourMinute(String(dailyTimes[0])));
	}

	const weekdayTimes = schedule.weekdayTimes as string[] | null | undefined;
	if (Array.isArray(weekdayTimes) && weekdayTimes[0]) {
		return capitalizeFirstWord(toHourMinute(String(weekdayTimes[0])));
	}

	const weeklyTimes = schedule.weeklyTimes as Record<string, string[]> | null | undefined;
	if (weeklyTimes && typeof weeklyTimes === 'object') {
		const firstDay = Object.keys(weeklyTimes)[0];
		const times = firstDay ? weeklyTimes[firstDay] : undefined;
		if (Array.isArray(times) && times[0]) {
			return capitalizeFirstWord(
				`${firstDay.charAt(0).toUpperCase()}${firstDay.slice(1)} ${toHourMinute(String(times[0]))}`
			);
		}
	}

	const recurringDays = schedule.monthlyScheduleRecurringDays as
		| Array<{ recurring?: number; day?: string; times?: string[] }>
		| null
		| undefined;
	if (
		schedule.monthlyScheduleType === 'recurring_days' &&
		Array.isArray(recurringDays) &&
		recurringDays[0]
	) {
		const week = getWeekFromRecurringNumber(Number(recurringDays[0].recurring));
		const day = capitalizeDay(String(recurringDays[0].day ?? ''));
		const time = recurringDays[0].times?.[0] ? toHourMinute(String(recurringDays[0].times[0])) : '';
		return capitalizeFirstWord(time ? `${week} ${day} at ${time}` : `${week} ${day}`);
	}

	const monthlyDates = schedule.monthlyScheduleSpecificDates as
		| Array<{ date?: string; times?: string[] }>
		| null
		| undefined;
	if (Array.isArray(monthlyDates) && monthlyDates[0]) {
		const day = monthlyDates[0].date ? String(monthlyDates[0].date).split('-')[2] : '';
		const time = monthlyDates[0].times?.[0] ? toHourMinute(String(monthlyDates[0].times[0])) : '';
		return capitalizeFirstWord(day && time ? `day ${day} at ${time}` : time || 'monthly');
	}

	if (schedule.annualDate && schedule.annualTime) {
		return capitalizeFirstWord(
			`${String(schedule.annualDate).split('T')[0]} ${toHourMinute(String(schedule.annualTime))}`
		);
	}

	return 'N/A';
};

export const validateRecurrenceForm = (
	recurrence: RecurrenceFormState
): Partial<Record<keyof RecurrenceFormState | 'scheduledTime', string>> => {
	const errors: Partial<Record<keyof RecurrenceFormState | 'scheduledTime', string>> = {};

	if (!recurrence.scheduledTime) {
		errors.scheduledTime = 'Time is required for recurring to-dos';
	}

	if (recurrence.frequencyLabel === 'Weekly' && !recurrence.dayOfWeek) {
		errors.dayOfWeek = 'Day of week is required';
	}

	if (recurrence.frequencyLabel === 'Monthly') {
		if (recurrence.monthlyScheduleType === 'specific_dates') {
			if (!recurrence.dayOfMonth || recurrence.dayOfMonth < 1 || recurrence.dayOfMonth > 31) {
				errors.dayOfMonth = 'Day of month must be between 1 and 31';
			}
		} else {
			if (!recurrence.monthPatternWeek) {
				errors.monthPatternWeek = 'Week of month is required';
			}
			if (!recurrence.monthPatternDay) {
				errors.monthPatternDay = 'Day of week is required';
			}
		}
	}

	if (recurrence.frequencyLabel === 'Annually') {
		if (!recurrence.annualMonth || recurrence.annualMonth < 1 || recurrence.annualMonth > 12) {
			errors.annualMonth = 'Month is required';
		}
		if (!recurrence.annualDay || recurrence.annualDay < 1 || recurrence.annualDay > 31) {
			errors.annualDay = 'Day is required';
		}
	}

	return errors;
};
